import { datadogRum } from '@datadog/browser-rum';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { convertKeysToCamelCase } from '@src/utilities/format';

// cached storage for the access token
let accessTokenCache: string | undefined;

export enum ENROLLMENT_ROUTES {
  ENROLLMENT_INTENTIONS = '/api/v2/enrollment_intentions',
  V1_ENROLLMENT_INTENTIONS = '/api/v1/enrollment_intentions_history',
}
export interface Enrollments {
  userUuid: string;
  usersEnrollments: UserEnrollment[];
}
export interface UserEnrollment {
  applicationUuid: string;
  enrollmentHistory: EnrollmentIntentions[];
}

export interface EnrollmentIntentions {
  enrollmentUuid: string;
  intendsToEnroll: null | boolean;
  recordedAt: null | string;
  isExpired: null | boolean;
  term: null | EnrollmentIntentionsTerm;
  newTerm?: null | EnrollmentIntentionsTerm;
  maxEnrollmentsAllowed: number;
}

export interface EnrollmentIntentionsTerm {
  id?: string;
  code: string;
  name: null | string;
  startDate: null | string;
  endDate: null | string;
}

export interface EnrollmentServiceRequestData {
  academic_partner_uuid: string;
  application_uuid: string;
  enrollment_uuid: string;
  intends_to_enroll: boolean;
  program_uuid: string;
  term_code: string;
  term_id?: string;
  user_uuid: string;
  start_date?: string;
  end_date?: string;
}

export interface V1EnrollmentServiceRequestData {
  user_uuid: string;
}

const getToken = async (getAccessToken: () => Promise<string | undefined>) => {
  try {
    if (!accessTokenCache) {
      accessTokenCache = await getAccessToken();
    }
  } catch (error) {
    accessTokenCache = undefined;
  }
};

export const createClient = (
  getAccessToken: () => Promise<string | undefined>,
  route = ENROLLMENT_ROUTES.ENROLLMENT_INTENTIONS
) => async (axiosArgs: AxiosRequestConfig) => {
  const axiosClient = axios.create({
    baseURL: `${process.env.REACT_APP_ENROLLMENT_SERVICE_URL}${route}`,
  });

  axiosClient?.interceptors?.response?.use((response) => response?.data, interceptorErrorHandler());

  await getToken(getAccessToken);
  axiosArgs.headers = {
    ...axiosArgs.headers,
    Authorization: `Bearer ${accessTokenCache}`,
  };

  return axiosClient(axiosArgs);
};

export const interceptorErrorHandler = () => {
  // return a function for use as the interceptor error handler
  return (error: AxiosError) => {
    if (error?.response?.status === 401) {
      accessTokenCache = undefined;
      throw error;
    } else {
      datadogRum.addError(error, {
        error: `Enrollment Service Error: ${error.message}`,
        stack: error.stack,
      });
      throw error;
    }
  };
};

export const useEnrollmentService = (
  getAccessToken: () => Promise<string | undefined>,
  route?: ENROLLMENT_ROUTES
) => {
  const client = createClient(getAccessToken, route);

  function post(data: EnrollmentServiceRequestData) {
    const axiosArgs = {
      method: 'post' as AxiosRequestConfig['method'],
      data,
    };
    return client(axiosArgs);
  }

  async function get(params: V1EnrollmentServiceRequestData) {
    const axiosArgs = {
      method: 'get' as AxiosRequestConfig['method'],
      params,
    };
    const response = await client(axiosArgs);
    return convertKeysToCamelCase(response) as Enrollments;
  }

  return {
    post,
    get,
  };
};
